import React, { FunctionComponent, SVGProps, useEffect, useState } from "react";
import styled, { css } from "styled-components";
import { breakpoints } from "themes/breakpoints";
import GenericButton from "ui/Button";

interface OptionProps {
  label: string;
  value: string;
  Icon?: FunctionComponent<SVGProps<SVGSVGElement>>;
}

interface ToggleButtonsProps {
  options: Array<OptionProps>;
  selectedValue: string;
  onClick: (value: string) => void;
  buttonWidth?: number;
  buttonHeight?: number;
}

const innerPadding = 3;
const borderRadius = 30;

const ToggleButton: React.FC<ToggleButtonsProps> = ({
  options,
  selectedValue,
  onClick,
  buttonWidth = 220,
  buttonHeight = 32,
}) => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const handleResize = () => {
    setWindowWidth(window.innerWidth);
  };

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const responsiveButtonWidth = Math.min(
    buttonWidth,
    Math.floor((windowWidth * 0.75) / options.length),
  );

  const selectIndex = options.findIndex((option) => option.value === selectedValue);
  const maskPosition = selectIndex * responsiveButtonWidth;

  const selectedButton = options[selectIndex];
  const SelectedButtonIcon = options[selectIndex].Icon;

  return (
    <Container height={buttonHeight}>
      {options.map(({ value, label, Icon }) => (
        <Button
          Icon={Icon ? <Icon /> : undefined}
          buttonWidth={responsiveButtonWidth}
          onClick={() => onClick(value)}
          key={value}
          isSelected={false}
        >
          {label}
        </Button>
      ))}
      <Mask buttonWidth={responsiveButtonWidth} translateX={maskPosition} isSelected>
        <Button
          Icon={SelectedButtonIcon ? <SelectedButtonIcon /> : undefined}
          buttonWidth={responsiveButtonWidth}
          isSelected
        >
          {selectedButton.label}
        </Button>
      </Mask>
    </Container>
  );
};

interface ButtonProps {
  isSelected: boolean;
  buttonWidth: number;
}

const buttonStyle = css<ButtonProps>`
  width: ${({ buttonWidth }) => buttonWidth}px;
  border-radius: ${borderRadius}px;
  align-content: center;
  justify-content: center;
  padding: 0;
  text-align: center;
  height: 100%;
  background-color: unset;

  .button-label {
    text-align: center;
    color: ${({ isSelected, theme }) =>
      theme.colors.buttonGroup[isSelected ? "active" : "inactive"].text};

    @media (max-width: ${breakpoints.sm}) {
      width: fit-content;
    }
  }

  .button-icon {
    fill: ${({ isSelected, theme }) =>
      theme.colors.buttonGroup[isSelected ? "active" : "inactive"].icon};
  }

  :hover {
    background: unset;
  }

  :focus {
    background: unset;
  }
`;

const Container = styled.div<{ height: number }>`
  position: relative;
  flex-direction: row;
  justify-content: center;
  align-self: center;
  border-radius: ${borderRadius}px;
  height: ${({ height }) => height}px;
  border: 1px solid ${({ theme }) => theme.colors.buttonGroup.active.background};
  padding: ${innerPadding}px;
`;

const Button = styled(GenericButton)<ButtonProps>`
  ${buttonStyle}
`;

const Mask = styled.div<{ translateX: number } & ButtonProps>`
  ${buttonStyle}
  background-color: ${({ theme }) => theme.colors.buttonGroup.active.background};
  position: absolute;
  height: calc(100% - ${2 * innerPadding}px);
  border-radius: ${borderRadius}px;
  top: ${innerPadding}px;
  transform: ${({ translateX }) => `translateX(${translateX}px)`};
  transition: 0.2s ease-in-out;

  :hover {
    background-color: ${({ theme }) => theme.colors.buttonGroup.active.background};
  }
`;

export default ToggleButton;
